import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Info, Calendar, Tag } from "lucide-react";
import { Policy } from "@/hooks/usePolicies";
import { format } from "date-fns";

interface PresetGeneralTabProps {
  preset: Policy;
}

export const PresetGeneralTab = ({ preset }: PresetGeneralTabProps) => {
  const createdDate = format(new Date(preset._creationTime), 'MMM d, yyyy');

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Info className="w-5 h-5" />
            <span>Basic Information</span>
          </CardTitle>
          <CardDescription>
            General details about this policy
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Name</label>
              <p className="text-sm text-gray-900 mt-1">{preset.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Type</label>
              <div className="mt-1">
                <Badge variant="outline">Policy</Badge>
              </div>
            </div>
          </div>
          
          {preset.description && (
            <div>
              <label className="text-sm font-medium text-gray-700">Description</label>
              <p className="text-sm text-gray-900 mt-1">{preset.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Policy Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Info className="w-5 h-5" />
            <span>Policy Statistics</span>
          </CardTitle>
          <CardDescription>
            Content included in this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{preset.categories.length}</div>
              <div className="text-sm text-gray-600">Categories</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {preset.allowlist?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Allowed Items</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {preset.blocklist?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Blocked Items</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5" />
            <span>Timeline</span>
          </CardTitle>
          <CardDescription>
            Important dates for this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Created</span>
              <span className="text-sm font-medium">{createdDate}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Tag className="w-5 h-5" />
            <span>Categories</span>
          </CardTitle>
          <CardDescription>
            Website categories included in this policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {preset.categories.map((category, index) => (
              <Badge key={index} variant="outline">
                {category}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};